import { Suspense, useCallback, useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

import { Bubbles } from "@/components/engine/Bubbles";
import { CausticLight } from "@/components/engine/CausticLight";
import { CalendarModel } from "@/components/engine/OfficeCalendar";
import { ComputerModel } from "@/components/engine/OfficeComputer";
import { FileCabinetModel } from "@/components/engine/OfficeFileCabinet";
import { MailboxModel } from "@/components/engine/OfficeMailbox";
import { TelephoneModel } from "@/components/engine/OfficeTelephone";
import { Pebbles } from "@/components/engine/Pebbles";
import { ProceduralCorals } from "@/components/engine/ProceduralCorals";
import { SandPlatform } from "@/components/engine/SandPlatform";
import { SleepPatchModel } from "@/components/engine/SleepPatch";

import { HeroLobster } from "./HeroLobster";
import { HomeProp } from "./HomeProp";
import { HERO_PROPS } from "./heroWorldConfig";
import type { HeroObjectId, HeroPropId } from "./heroWorldConfig";

import type { Vector3Tuple } from "three";

const PROP_MODELS: Record<HeroObjectId, React.ComponentType> = {
  mailbox: MailboxModel,
  computer: ComputerModel,
  fileCabinet: FileCabinetModel,
  telephone: TelephoneModel,
  calendar: CalendarModel,
  sleepPatch: SleepPatchModel,
};

interface HeroWorldProps {
  /** Fired with client coords on hover/tap; null when the pointer leaves. */
  onHover: (id: HeroPropId | null, x?: number, y?: number) => void;
  onClawbertTap: () => void;
  /** Caption pinned to Clawbert, following him through the scene. */
  clawbertCaption?: string;
}

/**
 * Fixed cinematic camera with a slow water-drift and gentle pointer
 * parallax. Closer on landscape so the island fills the frame; pulled back
 * and raised on portrait so the key objects stay in view on phones.
 */
function CameraRig() {
  const { camera, size } = useThree();
  const portrait = size.width < size.height;

  const base = useMemo(
    () =>
      portrait
        ? new THREE.Vector3(11.6, 9.2, 11.6)
        : new THREE.Vector3(9.6, 7.5, 9.6),
    [portrait],
  );

  useFrame(({ clock, pointer }) => {
    const t = clock.elapsedTime;
    const driftX = Math.sin(t * 0.14) * 0.35;
    const driftY = Math.sin(t * 0.22) * 0.25;
    camera.position.x += (base.x + driftX + pointer.x * 0.5 - camera.position.x) * 0.03;
    camera.position.y += (base.y + driftY - pointer.y * 0.35 - camera.position.y) * 0.03;
    camera.position.z += (base.z - camera.position.z) * 0.03;
    camera.lookAt(0, -0.2, 0);
  });

  return null;
}

/**
 * Monument Valley grade: ambient dominates so colors stay bright and flat,
 * the sun is gentle, and cast shadows read as soft pastel tints rather than
 * dark blobs. Kept identical to the in-app EnvironmentSetup.
 */
function HeroEnvironment() {
  return (
    <>
      <ambientLight intensity={1.05} color="#E9F0F4" />
      <directionalLight
        position={[8, 15, 5]}
        intensity={0.85}
        color="#FFF6E8"
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-bias={-0.001}
        shadow-normalBias={0.02}
        shadow-radius={8}
        shadow-camera-left={-12}
        shadow-camera-right={12}
        shadow-camera-top={12}
        shadow-camera-bottom={-12}
      />
      <directionalLight position={[-5, 8, -3]} intensity={0.4} color="#BCE0EE" />
      <fog attach="fog" args={["#5B8FA8", 26, 46]} />
      <color attach="background" args={["#5B8FA8"]} />
    </>
  );
}

export function HeroWorld({ onHover, onClawbertTap, clawbertCaption }: HeroWorldProps) {
  // Clawbert wanders on his own; nothing external sets his destination.
  const walkTargetRef = useRef<Vector3Tuple | null>(null);

  const handleClawbertHover = useCallback(
    (x: number, y: number) => onHover("clawbert", x, y),
    [onHover],
  );
  const handleClawbertLeave = useCallback(() => onHover(null), [onHover]);

  return (
    <Canvas
      shadows="soft"
      dpr={[1, 1.75]}
      camera={{ position: [9.6, 7.5, 9.6], fov: 42, near: 0.1, far: 120 }}
      gl={{ toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.2 }}
    >
      <Suspense fallback={null}>
        <CameraRig />
        <HeroEnvironment />
        <SandPlatform />
        <CausticLight size={15} intensity={0.2} scale={4.0} color="#B0C4DE" />
        <ProceduralCorals />
        <Bubbles />
        <Pebbles />
        {HERO_PROPS.map(({ id, position }) => {
          const Model = PROP_MODELS[id];
          // The sleep patch is scenery, not a feature — no tooltip, no glow.
          if (id === "sleepPatch") {
            return (
              <group key={id} position={position}>
                <Model />
              </group>
            );
          }
          return (
            <HomeProp key={id} id={id} position={position} onHover={onHover}>
              <Model />
            </HomeProp>
          );
        })}
        <HeroLobster
          walkTargetRef={walkTargetRef}
          onTap={onClawbertTap}
          onHoverStart={handleClawbertHover}
          onHoverEnd={handleClawbertLeave}
          caption={clawbertCaption}
        />
      </Suspense>
    </Canvas>
  );
}
